import { useCallback, useEffect, useState } from 'react'
import { getWafCaptchaConfiguration, getWafCaptchaScriptUrl } from '../utils/wafCaptcha'

type WafCaptchaStatus = 'disabled' | 'loading' | 'ready' | 'verified' | 'error'

const SCRIPT_ATTRIBUTE = 'data-spine-aws-waf-captcha'

/**
 * Loads and renders AWS WAF CAPTCHA while its container is active.
 */
export default function useWafCaptcha(isActive = true, onCaptchaSuccess?: () => void) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null)
  const [status, setStatus] = useState<WafCaptchaStatus>(() =>
    getWafCaptchaConfiguration().enabled && isActive ? 'loading' : 'disabled'
  )
  const [renderAttempt, setRenderAttempt] = useState(0)

  const configuration = getWafCaptchaConfiguration()

  const containerRef = useCallback((element: HTMLDivElement | null) => {
    setContainer(element)
  }, [])

  const retry = useCallback(() => {
    setStatus('loading')
    setRenderAttempt((attempt) => attempt + 1)
  }, [])

  const getToken = useCallback(async (): Promise<string | undefined> => {
    if (!configuration.enabled) {
      return undefined
    }

    const integration = window.AwsWafIntegration
    if (!integration) {
      throw new Error('AWS WAF integration is not available.')
    }

    return integration.getToken()
  }, [configuration.enabled])

  useEffect(() => {
    if (!configuration.enabled || !isActive) {
      setStatus('disabled')
      return
    }

    if (!container) {
      setStatus('loading')
      return
    }

    const scriptUrl = getWafCaptchaScriptUrl()
    if (!scriptUrl || !configuration.apiKey) {
      setStatus('error')
      return
    }

    const apiKey = configuration.apiKey

    let isMounted = true

    const renderCaptcha = () => {
      if (!isMounted || !window.AwsWafCaptcha) {
        if (isMounted) {
          setStatus('error')
        }
        return
      }

      container.replaceChildren()
      setStatus('ready')
      window.AwsWafCaptcha.renderCaptcha(container, {
        apiKey,
        onSuccess: () => {
          if (isMounted) {
            setStatus('verified')
            onCaptchaSuccess?.()
          }
        },
        onError: () => {
          if (isMounted) {
            setStatus('error')
          }
        }
      })
    }

    if (window.AwsWafCaptcha) {
      renderCaptcha()
      return () => {
        isMounted = false
      }
    }

    const existingScript = document.querySelector<HTMLScriptElement>(`script[${SCRIPT_ATTRIBUTE}]`)
    const script = existingScript ?? document.createElement('script')
    let scriptReady = false

    const handleLoad = () => {
      scriptReady = Boolean(window.AwsWafCaptcha)
      renderCaptcha()
    }
    const handleError = () => {
      if (isMounted) {
        setStatus('error')
      }
    }

    if (!existingScript) {
      script.src = scriptUrl
      script.defer = true
      script.setAttribute(SCRIPT_ATTRIBUTE, 'true')
    }

    script.addEventListener('load', handleLoad)
    script.addEventListener('error', handleError)

    if (!existingScript) {
      document.head.appendChild(script)
    }

    return () => {
      isMounted = false
      script.removeEventListener('load', handleLoad)
      script.removeEventListener('error', handleError)

      if (!scriptReady && !window.AwsWafCaptcha) {
        script.remove()
      }
    }
  }, [
    configuration.apiKey,
    configuration.enabled,
    container,
    isActive,
    onCaptchaSuccess,
    renderAttempt
  ])

  return {
    containerRef,
    enabled: configuration.enabled,
    isLoading: status === 'loading',
    isVerified: status === 'verified',
    hasError: status === 'error',
    getToken,
    retry
  }
}
